// ════════════════════════════════════════════════════════════
//  skybox.js — THE SKY BOX's shared state + its little piece of Firebase
// ════════════════════════════════════════════════════════════
//  ONE Sky Box per page, so its state is a module (like sculptGate): the 3D box,
//  the camera, the controls, the chat and App all read and write `BOX` — no React
//  state from a frame, no props threaded through the memo'd BattleScene.
//
//  ── THE DATA (all under the open games/shatterproof drawer — no rules change) ──
//   games/shatterproof/skybox/{boxId}/{uid}   one row per person IN a box (yours only)
//       { n name, b body f|m, m match, x z yaw (box-local), s seat (-1 = standing),
//         g sign text, gb sign board, u sign up 0|1, c chat text, ct chat time,
//         e emote, et emote time, t heartbeat }      — removed when you leave / drop
//   games/shatterproof/where/{uid}            { box, match, name, at, p, w } while you
//       watch an online fight — how a friend finds you ("Sit with them").
//       p 1 = a public box · w 1 = waiting for your partner there.
//       While you FIGHT an online fight it is { match, f: 1, room, name, at } instead
//       (no box: a fighter isn't in one) — so a friend can come and watch.
//  ⭐ TWO KINDS OF BOX (caP'n 2026-10-18):
//    PUBLIC (where everyone lands): `pub_{match}_{n}` — strangers on the same fight share
//      it; when one fills (SKY.seats) the next n opens (SKY_NET.pubBoxes of them).
//    PRIVATE (the switch in the box): its OWNER's uid — just you and the friends you
//      invite / who "Sit with you".
//  Joining a friend puts you in whatever box THEY are in (public or private).
//  Only people watching the SAME fight are drawn.
//
//  ⚠ Testing-phase trust, like the rest of the drawer: a signed-in client could
//  write someone else's row. Fine for caP'n and friends (PRIME §2b).
// ════════════════════════════════════════════════════════════
import { useEffect, useState } from "react";
import { ref, onValue, set, remove, onDisconnect, get } from "firebase/database";
import { db } from "../firebase";
import { BASE, serverNow } from "./ie.js";
import { SKY, SIGN, SKY_NET, SIGN_BOARDS, seatSpot, EMOTES, START, BOX_MODES } from "../data/skybox.js";

const LS = "shatterproof.skybox.v1";
const readLS = () => { try { return JSON.parse(localStorage.getItem(LS) || "{}") || {}; } catch (e) { return {}; } };
const writeLS = (v) => { try { localStorage.setItem(LS, JSON.stringify({ ...readLS(), ...v })); } catch (e) { /* private window: not remembered */ } };

export const cleanSign = (s) => String(s || "").replace(/\p{Cc}/gu, " ").replace(/\s+/g, " ").trim().slice(0, SIGN.max);

const saved = readLS();
let bodyChosen = saved.body === "m" || saved.body === "f";   // until you pick one, your body follows your profile
// ── THE ONE STATE ────────────────────────────────────────────
export const BOX = {
  uid: null, name: "", writable: false,
  mode: BOX_MODES.includes(saved.mode) ? saved.mode : BOX_MODES[0],   // where I go by myself: "public" | "private" (remembered)
  box: null,            // a box I was taken into (a friend's: their uid or a public box's id); null = my own kind
  hostUid: null,        // the friend whose box that is (I'm their guest) — "they moved on" watches them
  pub: null, pubFor: null,   // the public box picked for this fight (resolved from Firebase), and which fight it was for
  fighting: false,      // I'm a FIGHTER in the online fight on screen (no box; friends can come and watch)
  room: null,           // the room that fight came from (so a friend joins the room and watches from there)
  waiting: false,       // waiting for my partner in this box (Lyraei Plays: "wait for partner")
  match: null,          // the online fight on screen (null = an offline watch: the box is just yours)
  inside: false,        // your body is in the Sky Box (all of a fight / ringside; the camera view is a separate choice)
  me: {
    x: START.x, z: START.z, yaw: 0, moving: false, seat: -1,
    body: saved.body === "m" || saved.body === "f" ? saved.body : "f",
    sign: cleanSign(saved.sign || ""), board: SIGN_BOARDS.some((b) => b.id === saved.board && b.free) ? saved.board : SIGN_BOARDS[0].id,
    signUp: false, chat: "", chatAt: 0, emote: null, emoteAt: 0,
  },
  folk: {},             // uid → their row (+ cx/cz/cyaw: what's drawn, eased toward x/z/yaw)
  host: null,           // the box owner's `where` (when we're a guest): { match, name } — "they moved on"
  keys: new Set(),      // movement keys held (the controls fill it, the body reads it)
  ui: {},               // App registers openMark / openFriends / toast / sitWith here
};
const subs = new Set();
const ping = () => subs.forEach((f) => { try { f(); } catch (e) { /* a bad listener never stops the rest */ } });
let ver = 0;
const bump = () => { ver++; ping(); };
// re-render a React piece when the roster or my own choices change (never per frame)
export function useBox() {
  const [, setV] = useState(0);
  useEffect(() => { const f = () => setV(ver); subs.add(f); return () => subs.delete(f); }, []);
  return BOX;
}

export const pubId = (match, n) => `pub_${match}_${n}`;
export const isPublicBox = (id) => typeof id === "string" && id.startsWith("pub_");
// the box I'm in: one I was taken into, else my own kind (null while a public box is still being picked)
export const boxId = () => BOX.box || (BOX.mode === "private" ? BOX.uid : BOX.pubFor === BOX.match ? BOX.pub : null);
export const isGuest = () => !!(BOX.hostUid && BOX.uid && BOX.hostUid !== BOX.uid);
export const inPublic = () => isPublicBox(boxId());
const rowPath = (box, uid) => `${BASE}/skybox/${box}/${uid}`;
const wherePath = (uid) => `${BASE}/where/${uid}`;
const live = () => !!(BOX.writable && BOX.uid && BOX.match && !BOX.fighting);   // a fighter has no box

// ── who's here, as a list (me first) ─────────────────────────
export function people() {
  const out = [{ uid: BOX.uid || "me", me: true, n: BOX.name || "You", s: BOX.me.seat }];
  Object.entries(BOX.folk).forEach(([uid, f]) => out.push({ uid, me: false, n: f.n, s: f.s }));
  return out;
}
export const seatTaken = (i) => Object.values(BOX.folk).some((f) => f.s === i);
export const isFull = () => Object.keys(BOX.folk).length + 1 > SKY.seats;

// ── writing my row ───────────────────────────────────────────
let lastSent = 0, lastBeat = 0, sentKey = "";
function row() {
  const m = BOX.me;
  return {
    n: String(BOX.name || "Unclaimed").slice(0, 24), b: m.body || "f", m: BOX.match,
    x: Math.round(m.x * 100) / 100, z: Math.round(m.z * 100) / 100, yaw: Math.round(m.yaw * 100) / 100,
    s: m.seat, g: m.sign, gb: m.board, u: m.signUp ? 1 : 0, c: m.chat, ct: m.chatAt, e: m.emote || "", et: m.emoteAt, t: serverNow(),
  };
}
export const myRow = row;   // my own row, in the same shape as everyone else's (for drawing my sign)
// called every frame by the body; it only really sends when something changed (throttled) or the heartbeat is due
export function publish(force = false) {
  if (!live() || !BOX.inside || !boxId()) return;
  const now = Date.now(), r = row();
  const key = `${r.m}|${r.x}|${r.z}|${r.yaw}|${r.s}|${r.g}|${r.gb}|${r.u}|${r.ct}|${r.et}|${r.b}|${r.n}`;   // the match is in it: a new fight is always sent at once (review 2026-10-17)
  const changed = key !== sentKey;
  if (!force && !(changed && now - lastSent >= SKY_NET.sendGap) && now - lastBeat < SKY_NET.beat) return;
  sentKey = key; lastSent = now; lastBeat = now;
  set(ref(db, rowPath(boxId(), BOX.uid)), r).catch(() => {});
}

// ── the box's listener ───────────────────────────────────────
let off = [], listening = "";
function stopListening() { off.forEach((f) => { try { f(); } catch (e) { /* gone */ } }); off = []; listening = ""; }
function listen() {
  const want = live() && boxId() ? `${boxId()}|${BOX.match}` : "";
  if (want === listening) return;
  stopListening();
  BOX.folk = {}; BOX.host = null;
  if (!want) { bump(); return; }
  listening = want;
  off.push(onValue(ref(db, `${BASE}/skybox/${boxId()}`), (snap) => {
    const now = serverNow(), next = {};
    snap.forEach((c) => {
      const v = c.val();
      if (!v || c.key === BOX.uid || v.m !== BOX.match || !(now - (v.t || 0) < SKY_NET.stale)) return;
      if (!Number.isFinite(v.x) || !Number.isFinite(v.z)) return;                      // a broken row is never drawn
      v.s = Number.isInteger(v.s) && v.s >= 0 && v.s < SKY.seats ? v.s : -1;
      if (!Number.isFinite(v.yaw)) v.yaw = 0;
      const was = BOX.folk[c.key];
      next[c.key] = { ...v, cx: was ? was.cx : v.x, cz: was ? was.cz : v.z, cyaw: was ? was.cyaw : v.yaw };
    });
    const sig = (o) => Object.keys(o).sort().map((k) => `${k}:${o[k].n}:${o[k].b}:${o[k].s}`).join(",");
    const rosterChanged = sig(next) !== sig(BOX.folk);
    BOX.folk = next;
    // two people grabbed the same seat at once: the earlier uid keeps it, I get up
    if (BOX.me.seat >= 0 && Object.entries(next).some(([u, f]) => f.s === BOX.me.seat && u < BOX.uid)) { BOX.me.seat = -1; publish(true); bump(); return; }
    if (rosterChanged) bump();
  }, () => {}));
  if (isGuest()) {
    off.push(onValue(ref(db, wherePath(BOX.hostUid)), (s) => { BOX.host = s.val() || null; bump(); }, () => {}));
  }
}

// ── where I am (for friends) ─────────────────────────────────
let whereKey = "";
function writeWhere() {
  if (!(BOX.writable && BOX.uid)) return;
  const fight = BOX.match && BOX.fighting, box = fight ? null : boxId();
  // a spectator whose public box is still being picked writes nothing yet (sync runs again once it's picked)
  const key = !BOX.match || (!fight && !box) ? "" : fight ? `f|${BOX.match}|${BOX.room}|${BOX.name}` : `${box}|${BOX.match}|${BOX.name}|${BOX.waiting ? 1 : 0}`;
  if (key === whereKey) return;
  whereKey = key;
  const r = ref(db, wherePath(BOX.uid));
  if (!key) { remove(r).catch(() => {}); return; }
  onDisconnect(r).remove().catch(() => {});
  const name = String(BOX.name || "").slice(0, 24), at = serverNow();
  set(r, fight ? { match: BOX.match, f: 1, room: BOX.room || null, name, at }
               : { box, match: BOX.match, name, at, p: isPublicBox(box) ? 1 : 0, w: BOX.waiting ? 1 : 0 }).catch(() => {});
}
// ── picking a public box for this fight: the first one with a free seat ──
let pubTok = 0;
const needPub = () => live() && !BOX.box && BOX.mode === "public" && BOX.pubFor !== BOX.match;
async function resolvePub() {
  const m = BOX.match, tok = ++pubTok;
  BOX.pubFor = m; BOX.pub = null;
  let pick = null;
  for (let n = 1; n <= SKY_NET.pubBoxes && !pick; n++) {
    let rows = {};
    try { rows = (await get(ref(db, `${BASE}/skybox/${pubId(m, n)}`))).val() || {}; } catch (e) { /* unreadable: treat as empty */ }
    const now = serverNow();
    const here = Object.entries(rows).filter(([u, r]) => u !== BOX.uid && r && r.m === m && now - (r.t || 0) < SKY_NET.stale).length;
    if (here < SKY.seats) pick = pubId(m, n);
  }
  if (tok !== pubTok || BOX.match !== m) return;            // the fight changed while we looked: that look is old news
  BOX.pub = pick || pubId(m, SKY_NET.pubBoxes);            // every one full: the last one just gets crowded (stand and enjoy it)
  sync();
}
let rowAt = "";
function clearRow() {
  if (!rowAt) return;
  remove(ref(db, rowAt)).catch(() => {});
  rowAt = "";
}
function placeRow() {
  const p = live() && BOX.inside && boxId() ? rowPath(boxId(), BOX.uid) : "";
  if (p === rowAt) return;
  clearRow();
  if (!p) return;
  rowAt = p;
  onDisconnect(ref(db, p)).remove().catch(() => {});
  sentKey = ""; publish(true);
}
function sync() { if (needPub()) resolvePub(); writeWhere(); listen(); placeRow(); bump(); }

// ── a dropped connection: Firebase already ran our onDisconnect (rows gone) and those handlers
// are spent — so on every reconnect, write everything again and re-arm them (review 2026-10-17)
let connArmed = false;
function armConnection() {
  if (connArmed) return; connArmed = true;
  onValue(ref(db, ".info/connected"), (s) => { if (s.val() === true) { whereKey = ""; rowAt = ""; sentKey = ""; sync(); } }, () => {});   // the public box picked for this fight is KEPT (friends sitting with me stay together — review 2026-10-18)
  // someone whose tab went to sleep stops beating: drop them even if no new snapshot arrives
  setInterval(() => {
    const now = serverNow(); let gone = false;
    Object.entries(BOX.folk).forEach(([u, f]) => { if (!(now - (f.t || 0) < SKY_NET.stale)) { delete BOX.folk[u]; gone = true; } });
    if (gone) bump();
  }, 5000);
}
// before signing out (while the account can still delete its own rows)
export function boxLeaveAll() { BOX.inside = false; BOX.match = null; BOX.box = null; BOX.hostUid = null; BOX.fighting = false; BOX.room = null; sync(); }

// ── the doors App and the controls use ───────────────────────
// who I am (App calls this whenever the account changes)
export function boxIdentity({ uid, name, writable, gender }) {
  if (typeof window !== "undefined") armConnection();
  const changed = uid !== BOX.uid;
  BOX.uid = uid || null; BOX.name = name || ""; BOX.writable = !!writable;
  if (!bodyChosen) BOX.me.body = gender === "m" ? "m" : "f";   // Terror's rule: male → Laostre, female → Lyraei
  if (changed) { BOX.box = null; BOX.hostUid = null; BOX.pubFor = null; whereKey = ""; }
  sync();
}
// a fight came on screen (match = its id; null for an offline watch) / went away (undefined)
// { fighting, room }: I'm a fighter in it (no box — but friends can find the fight), and the room it came from
export function boxFight(match, { fighting = false, room = null } = {}) {
  BOX.match = match || null;
  BOX.fighting = !!(match && fighting); BOX.room = BOX.fighting ? room || null : null;
  // a PUBLIC box belongs to one fight: on another fight it's stale (a friend I follow re-places me in theirs — App's follow)
  if (match && isPublicBox(BOX.box) && !BOX.box.startsWith(`pub_${match}_`)) { BOX.box = null; BOX.hostUid = null; }
  if (match === undefined) { BOX.inside = false; BOX.match = null; BOX.box = null; BOX.hostUid = null; }   // leaving the fights: you're back in your own box next time
  sentKey = "";
  sync();
}
// in or out of the Sky Box view
export function boxInside(on) {
  if (BOX.inside === !!on) return;
  BOX.inside = !!on;
  if (!on) { BOX.me.seat = -1; BOX.keys.clear(); }
  sync();
}
// move into a friend's box (box = its id: their uid or a public box; host = the friend), or back to my own (null)
export function boxMoveTo(box, host = null) {
  BOX.box = box && box !== BOX.uid ? box : null;
  BOX.hostUid = BOX.box && host && host !== BOX.uid ? host : null;
  BOX.me.seat = -1; BOX.me.x = START.x; BOX.me.z = START.z;
  sync();
}
// THE SWITCH: my own public box / my own private box (remembered; leaves a friend's box too)
export function setBoxMode(mode) {
  if (!BOX_MODES.includes(mode)) return;
  BOX.mode = mode; writeLS({ mode });
  BOX.box = null; BOX.hostUid = null;
  BOX.me.seat = -1; BOX.me.x = START.x; BOX.me.z = START.z;
  sync();
}
// waiting here for my partner (Lyraei Plays) — friends see "waiting" on my where
export function setWaiting(on) {
  if (BOX.waiting === !!on) return;
  BOX.waiting = !!on;
  sync();
  BOX.ui.onWaiting && BOX.ui.onWaiting(BOX.waiting);
}
// a friend's whereabouts → { box, match } or null
export async function whereIs(uid) {
  try {
    const w = (await get(ref(db, wherePath(uid)))).val();
    // the row is removed when they leave the fight or drop (onDisconnect); the age check only catches a crashed ghost
    if (!w || !w.match || !(serverNow() - (w.at || 0) < SKY_NET.whereStale)) return null;   // { box, match, name, at, p, w } or a fighter's { match, f, room, name, at }
    return w;
  } catch (e) { return null; }
}

// ── things I do ──────────────────────────────────────────────
export function sitNearest() {
  const m = BOX.me;
  let best = -1, bd = 1e9;
  for (let i = 0; i < SKY.seats; i++) {
    if (seatTaken(i)) continue;
    const p = seatSpot(i), d = Math.hypot(p.x - m.x, p.z - m.z);
    if (d < bd) { bd = d; best = i; }
  }
  if (best < 0) { BOX.ui.toast && BOX.ui.toast("Every seat's taken — stand and enjoy it"); return false; }
  m.seat = best; m.moving = false; BOX.keys.clear();
  publish(true); bump(); return true;
}
export function standUp() {
  const m = BOX.me;
  if (m.seat < 0) return;
  const p = seatSpot(m.seat);
  m.x = p.x; m.z = p.z - 0.95; m.yaw = 0; m.seat = -1;
  publish(true); bump();
}
export const toggleSit = () => (BOX.me.seat >= 0 ? standUp() : sitNearest());
export function setSign(text) { BOX.me.sign = cleanSign(text); writeLS({ sign: BOX.me.sign }); publish(true); bump(); }
export function setBoard(id) {
  if (!SIGN_BOARDS.some((b) => b.id === id && b.free)) return;   // the shop will open the rest
  BOX.me.board = id; writeLS({ board: id }); publish(true); bump();
}
export function toggleSignUp(v) { BOX.me.signUp = v == null ? !BOX.me.signUp : !!v; publish(true); bump(); }
export function setBody(b) { if (b !== "f" && b !== "m") return; BOX.me.body = b; bodyChosen = true; writeLS({ body: b }); publish(true); bump(); }
export function emote(id) {
  if (!EMOTES.some((e) => e.id === id)) return;
  BOX.me.emote = id; BOX.me.emoteAt = serverNow(); publish(true); bump();
}
// THE CHAT ON YOUR SIGN: what you just said in the world chat goes up on your board
export function sayOnSign(text) {
  const t = cleanSign(text);
  if (!t) return;
  BOX.me.chat = t; BOX.me.chatAt = serverNow(); publish(true); bump();
}
// what a sign shows right now: the last chat (for SIGN.chatShow s), else the written sign if it's up
export function signNow(p, now) {
  if (p.c && now - (p.ct || 0) < SIGN.chatShow * 1000) return { text: p.c, chat: true };
  if (p.u && p.g) return { text: p.g, chat: false };
  return null;
}

// console handle (like __circuit / __battleCue): __skybox.BOX, and what Firebase holds for my box / my "where"
if (typeof window !== "undefined") {
  window.__skybox = {
    BOX,
    read: async () => ({
      box: (await get(ref(db, `${BASE}/skybox/${boxId()}`))).val(),
      where: BOX.uid ? (await get(ref(db, wherePath(BOX.uid)))).val() : null,
    }),
    // DEV ONLY: a pretend friend in my box (seat 2, sign up, saying hi) — ghost(false) takes it away
    ghost: process.env.NODE_ENV !== "production" ? (on = true, seat = 2) => {
      if (!boxId()) return Promise.resolve("no box yet");
      const p = rowPath(boxId(), "dev-ghost");
      if (!on) return remove(ref(db, p));
      onDisconnect(ref(db, p)).remove().catch(() => {});
      return set(ref(db, p), { n: "Test Friend", b: "m", m: BOX.match, x: 1.5, z: 0.2, yaw: 0, s: seat, g: "HI CAP'N", gb: "sun", u: 1, c: "", ct: 0, e: "", et: 0, t: serverNow() });
    } : undefined,
  };
}
